import { supabase } from './supabase';

// Simple UUID v4 generator implementation
function uuidv4() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// Session ID management
const SESSION_ID_KEY = 'elev8_session_id';
const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes in milliseconds
const LAST_ACTIVITY_KEY = 'elev8_last_activity';

// Device detection
const isMobile = (): boolean => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
};

const isTablet = (): boolean => {
  return /(iPad|tablet|Tablet|Android(?!.*Mobile))/i.test(navigator.userAgent);
};

/**
 * Get or create a session ID for the current user
 */
export const getSessionId = (): string => {
  // Check for existing session ID in storage
  let sessionId = sessionStorage.getItem(SESSION_ID_KEY);
  const lastActivity = sessionStorage.getItem(LAST_ACTIVITY_KEY);
  const now = Date.now();

  // If no session ID or session expired, create a new one
  if (
    !sessionId ||
    !lastActivity ||
    now - parseInt(lastActivity) > SESSION_TIMEOUT
  ) {
    sessionId = uuidv4();
    sessionStorage.setItem(SESSION_ID_KEY, sessionId);
  }

  // Update last activity time
  sessionStorage.setItem(LAST_ACTIVITY_KEY, now.toString());

  return sessionId;
};

/**
 * Extract UTM parameters from URL
 */
export const getUtmParams = (): Record<string, string> => {
  const params: Record<string, string> = {};
  const urlParams = new URLSearchParams(window.location.search);

  // Get all UTM parameters
  [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
  ].forEach(param => {
    const value = urlParams.get(param);
    if (value) {
      params[param.replace('utm_', '')] = value;
    }
  });

  return params;
};

/**
 * Get browser and device information
 */
export const getDeviceInfo = (): Record<string, any> => {
  const ua = navigator.userAgent;
  const mobile = isMobile();
  const tablet = isTablet();

  // Determine browser
  let browser = 'Unknown';
  if (ua.indexOf('Firefox') > -1) browser = 'Firefox';
  else if (ua.indexOf('SamsungBrowser') > -1) browser = 'Samsung';
  else if (ua.indexOf('Opera') > -1 || ua.indexOf('OPR') > -1)
    browser = 'Opera';
  else if (ua.indexOf('Trident') > -1) browser = 'Internet Explorer';
  else if (ua.indexOf('Edge') > -1) browser = 'Edge';
  else if (ua.indexOf('Chrome') > -1) browser = 'Chrome';
  else if (ua.indexOf('Safari') > -1) browser = 'Safari';

  // Determine OS
  let os = 'Unknown';
  if (ua.indexOf('Windows') > -1) os = 'Windows';
  else if (ua.indexOf('Mac') > -1) os = 'MacOS';
  else if (ua.indexOf('X11') > -1) os = 'UNIX';
  else if (ua.indexOf('Linux') > -1) os = 'Linux';
  else if (ua.indexOf('Android') > -1) os = 'Android';
  else if (ua.indexOf('iOS') > -1) os = 'iOS';

  return {
    browser,
    operating_system: os,
    is_mobile: mobile,
    is_tablet: tablet,
    is_desktop: !mobile && !tablet,
    screen_size: `${window.innerWidth}x${window.innerHeight}`,
    device_type: mobile ? 'mobile' : tablet ? 'tablet' : 'desktop',
  };
};

// Time tracking for pages
let pageLoadTime = Date.now();
let isExitPageRegistered = false;

/**
 * Track a page visit
 * @param page The path of the page being visited
 * @param isEntry Whether this is the entry page
 */
export const trackPageVisit = async (
  page: string,
  isEntry = false
): Promise<void> => {
  // Reset timer when tracking a new page
  pageLoadTime = Date.now();
  isExitPageRegistered = false;

  try {
    const sessionId = getSessionId();
    const utmParams = getUtmParams();
    const deviceInfo = getDeviceInfo();

    // Track page visit in Supabase
    await supabase.from('user_analytics').insert({
      session_id: sessionId,
      page_visited: page,
      time_spent_seconds: 0, // Will be updated on exit
      referrer: document.referrer,
      utm_source: utmParams.source,
      utm_medium: utmParams.medium,
      utm_campaign: utmParams.campaign,
      utm_term: utmParams.term,
      utm_content: utmParams.content,
      ...deviceInfo,
      entry_page: isEntry,
      exit_page: false,
      ip_address: '', // This will be captured serverside
    });

    console.debug(`Analytics: Tracked visit to ${page}`);
  } catch (error) {
    console.error('Failed to track page visit:', error);
  }
};

/**
 * Track when a user leaves a page
 * @param page The path of the page being exited
 */
export const trackPageExit = async (page: string): Promise<void> => {
  // Only track exit once per page
  if (isExitPageRegistered) return;
  isExitPageRegistered = true;

  const timeSpent = Math.floor((Date.now() - pageLoadTime) / 1000);
  const sessionId = getSessionId();

  try {
    // First, update the time spent on the current page
    await supabase
      .from('user_analytics')
      .update({
        time_spent_seconds: timeSpent,
        exit_page: true,
      })
      .eq('session_id', sessionId)
      .eq('page_visited', page)
      .is('exit_page', false);

    console.debug(
      `Analytics: Tracked exit from ${page} (${timeSpent} seconds)`
    );
  } catch (error) {
    console.error('Failed to track page exit:', error);
  }
};

/**
 * Initialize analytics tracking for the application
 */
export const initAnalytics = (): void => {
  // Determine if this is an entry page
  const isEntry =
    !document.referrer || !document.referrer.includes(window.location.hostname);

  // Track the initial page visit
  trackPageVisit(window.location.pathname, isEntry);

  // Set up event listener for page exits
  window.addEventListener('beforeunload', () => {
    trackPageExit(window.location.pathname);
  });

  // Handle page navigation events for SPAs
  const originalPushState = history.pushState;
  const originalReplaceState = history.replaceState;

  // Track navigation via history API
  history.pushState = function (...args) {
    trackPageExit(window.location.pathname);
    originalPushState.apply(this, args);

    // After state change, track the new page
    setTimeout(() => {
      trackPageVisit(window.location.pathname);
    }, 0);
  };

  history.replaceState = function (...args) {
    trackPageExit(window.location.pathname);
    originalReplaceState.apply(this, args);

    // After state change, track the new page
    setTimeout(() => {
      trackPageVisit(window.location.pathname);
    }, 0);
  };

  // Handle popstate events (back/forward buttons)
  window.addEventListener('popstate', () => {
    trackPageExit(window.location.pathname);

    // After state change, track the new page
    setTimeout(() => {
      trackPageVisit(window.location.pathname);
    }, 0);
  });
};

/**
 * Add UTM tracking to a URL
 * @param url The URL to add tracking to
 * @param campaign The campaign name
 * @param source The traffic source
 * @param medium The marketing medium
 */
export const addUtmTracking = (
  url: string,
  campaign: string,
  source: string = 'website',
  medium: string = 'referral'
): string => {
  // Create URL object for easier manipulation
  const urlObj = new URL(url.startsWith('http') ? url : `https://${url}`);

  // Add UTM parameters
  urlObj.searchParams.set('utm_campaign', campaign);
  urlObj.searchParams.set('utm_source', source);
  urlObj.searchParams.set('utm_medium', medium);

  return urlObj.toString();
};

export default {
  initAnalytics,
  trackPageVisit,
  trackPageExit,
  getSessionId,
  getUtmParams,
  addUtmTracking,
};
